import { Eye, History, Plus, Save, Send, X } from "lucide-react";
import * as React from "react";

import { type FormDef, type FormFieldSchema, formsApi } from "@/api/forms";
import { Canvas } from "@/components/form-builder/Canvas";
import { CustomFieldDialog } from "@/components/form-builder/CustomFieldDialog";
import { FieldOverrideDialog } from "@/components/form-builder/FieldOverrideDialog";
import { LivePreview } from "@/components/form-builder/LivePreview";
import { Palette } from "@/components/form-builder/Palette";
import { PropertiesPanel } from "@/components/form-builder/PropertiesPanel";
import { PublishDialog } from "@/components/form-builder/PublishDialog";
import { VersionTimeline } from "@/components/form-builder/VersionTimeline";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingPage } from "@/components/ui/loading-page";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { type FieldPath, useFormBuilderStore } from "@/stores/formBuilder";
import { useRouteStore } from "@/stores/route";
import { useUIStore } from "@/stores/ui";

interface Props {
  code: string;
}

type SidePanel = "preview" | "history" | null;

export function FormBuilder({ code }: Props): JSX.Element {
  const navigate = useRouteStore((s) => s.navigate);
  const pushToast = useUIStore((s) => s.pushToast);
  const schema = useFormBuilderStore((s) => s.schema);
  const isDirty = useFormBuilderStore((s) => s.isDirty);
  const formName = useFormBuilderStore((s) => s.formName);
  const versionInfo = useFormBuilderStore((s) => s.versionInfo);
  const loadSchema = useFormBuilderStore((s) => s.loadSchema);
  const resetToInitial = useFormBuilderStore((s) => s.resetToInitial);

  const [form, setForm] = React.useState<FormDef | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [showPublish, setShowPublish] = React.useState(false);
  const [sidePanel, setSidePanel] = React.useState<SidePanel>(null);
  const [showCustomFields, setShowCustomFields] = React.useState(false);
  const [overrideTarget, setOverrideTarget] = React.useState<{
    path: FieldPath;
    field: FormFieldSchema;
  } | null>(null);

  const refreshDef = React.useCallback(async (): Promise<FormDef | null> => {
    try {
      const f = await formsApi.get(code);
      setForm(f);
      return f;
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi tải form",
        description: e instanceof Error ? e.message : String(e),
      });
      return null;
    }
  }, [code, pushToast]);

  const loadVersionIntoEditor = React.useCallback(
    async (def: FormDef, versionNumber: number): Promise<void> => {
      try {
        const v = await formsApi.getVersion(code, versionNumber);
        loadSchema(v.schema, {
          code: def.code,
          name: def.name,
          description: def.description ?? "",
          version: v.version,
          status: v.status,
        });
      } catch (e) {
        pushToast({
          variant: "destructive",
          title: "Không tải được version",
          description: e instanceof Error ? e.message : String(e),
        });
      }
    },
    [code, loadSchema, pushToast],
  );

  // Initial load
  React.useEffect(() => {
    void (async () => {
      setLoading(true);
      const def = await refreshDef();
      if (def) {
        // Mặc định mở draft mới nhất, nếu không có thì published
        const draft = def.versions.find((v) => v.status === "draft");
        const target = draft ?? def.versions.find((v) => v.status === "published");
        if (target) {
          await loadVersionIntoEditor(def, target.version);
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleSave = async (): Promise<void> => {
    if (!schema) return;
    setSaving(true);
    try {
      // Update name + description nếu thay đổi (qua tạo form mới hoặc keep schema)
      const v = await formsApi.saveDraft(code, {
        schema: { ...schema, code, title: formName },
        logic_rules: {},
        change_notes: null,
      });
      pushToast({
        variant: "success",
        title: `Đã save draft v${v.version}`,
      });
      const def = await refreshDef();
      if (def) {
        await loadVersionIntoEditor(def, v.version);
      }
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  // Keyboard shortcuts (P2.1-S5)
  useKeyboardShortcuts(
    {
      "cmd+s": () => {
        if (!saving && isDirty) void handleSave();
      },
      del: () => {
        const state = useFormBuilderStore.getState();
        if (state.selectedPath) state.removeField(state.selectedPath);
      },
      "cmd+d": () => {
        const state = useFormBuilderStore.getState();
        if (state.selectedPath) state.duplicateField(state.selectedPath);
      },
    },
    { enabled: !loading },
  );

  const handlePublishClick = (): void => {
    if (isDirty) {
      pushToast({
        variant: "warning",
        title: "Save Draft trước khi publish",
      });
      return;
    }
    if (versionInfo?.status === "published") {
      pushToast({
        variant: "warning",
        title: "Version này đã published rồi",
      });
      return;
    }
    setShowPublish(true);
  };

  if (loading) {
    return <LoadingPage label="Đang tải form..." />;
  }
  if (!form) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <span className="text-sm text-muted-foreground">
          Form <code>{code}</code> không tồn tại.
        </span>
        <Button variant="outline" onClick={() => navigate("admin.forms")}>
          ← Quay lại danh sách
        </Button>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Topbar */}
      <header className="h-12 shrink-0 border-b flex items-center px-3 gap-2 bg-background">
        <button
          onClick={() => navigate("admin.forms")}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← Forms
        </button>
        <div className="flex items-center gap-2 ml-2 min-w-0">
          <span className="font-semibold text-sm truncate">{formName}</span>
          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{code}</code>
          {versionInfo ? (
            <Badge
              variant={versionInfo.status === "published" ? "default" : "outline"}
              className="text-[10px]"
            >
              v{versionInfo.version} {versionInfo.status}
            </Badge>
          ) : null}
          {isDirty ? (
            <Badge variant="secondary" className="text-[10px]">
              · unsaved
            </Badge>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant={sidePanel === "preview" ? "default" : "outline"}
            onClick={() => setSidePanel(sidePanel === "preview" ? null : "preview")}
          >
            <Eye className="size-4 mr-1" /> Preview
          </Button>
          <Button
            size="sm"
            variant={sidePanel === "history" ? "default" : "outline"}
            onClick={() => setSidePanel(sidePanel === "history" ? null : "history")}
          >
            <History className="size-4 mr-1" /> Versions
          </Button>
          {isDirty ? (
            <Button size="sm" variant="ghost" onClick={resetToInitial}>
              Bỏ
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowCustomFields(true)}
          >
            <Plus className="size-4 mr-1" /> Custom field
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleSave()}
            disabled={saving || !isDirty}
          >
            <Save className="size-4 mr-1" />
            {saving ? "Đang lưu..." : "Save Draft"}
          </Button>
          <Button size="sm" onClick={handlePublishClick} disabled={saving}>
            <Send className="size-4 mr-1" /> Publish
          </Button>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex min-h-0">
        <Palette />
        <Canvas
          onOverride={(path, field) => setOverrideTarget({ path, field })}
        />
        {sidePanel === "preview" ? (
          <SidePane title="Live Preview" onClose={() => setSidePanel(null)}>
            <LivePreview />
          </SidePane>
        ) : sidePanel === "history" ? (
          <SidePane title="Versions" onClose={() => setSidePanel(null)}>
            <div className="p-3">
              <VersionTimeline
                form={form}
                onRefresh={async () => {
                  await refreshDef();
                }}
                onLoadVersion={async (v) => {
                  const def = await refreshDef();
                  if (def) await loadVersionIntoEditor(def, v);
                }}
              />
            </div>
          </SidePane>
        ) : null}
        <PropertiesPanel />
      </div>

      <PublishDialog
        open={showPublish}
        onClose={() => setShowPublish(false)}
        onPublished={async () => {
          await refreshDef();
        }}
      />

      <CustomFieldDialog
        open={showCustomFields}
        formCode={code}
        onClose={() => setShowCustomFields(false)}
        onChanged={async () => {
          await refreshDef();
        }}
      />

      <FieldOverrideDialog
        open={overrideTarget !== null}
        formCode={code}
        fieldName={overrideTarget?.field.name ?? ""}
        fieldLockLevel={
          (overrideTarget?.field as { lock_level?: string } | undefined)?.lock_level
        }
        onClose={() => setOverrideTarget(null)}
        onChanged={async () => {
          await refreshDef();
        }}
      />
    </div>
  );
}

function SidePane({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}): JSX.Element {
  return (
    <aside className="w-96 shrink-0 border-l bg-background overflow-y-auto">
      <div className="px-3 py-2 border-b flex items-center justify-between sticky top-0 bg-background z-10">
        <h2 className="text-xs font-semibold uppercase">{title}</h2>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
      {children}
    </aside>
  );
}
